import { createFileRoute, useLocation } from "@tanstack/react-router";
import React, { Suspense, lazy } from "react";

import {
  validateBuildingDetailSearch,
  type BuildingDetailRouteSearch,
} from "@/pages/Portal/BuildingDetail/tabs";

// Wave-17 Item 1.1 — lazy-load the BuildingDetail page (2.7k lines, the
// heaviest Portal surface). The route wrapper stays eager because it
// reads the route param via `Route.useParams()` before the page mounts;
// only the page module itself dynamic-imports, producing a dedicated
// chunk. <Suspense fallback={null}> mirrors the page's own
// BuildingDetailSkeleton — the skeleton renders inside the page once the
// chunk loads, so a `null` fallback during the brief in-flight chunk
// fetch keeps the visual handoff clean.
//
// `tabs.ts` is imported EAGERLY and that is deliberate — but NOT because
// it is dependency-free: it also holds `useBuildingTab`, so it imports
// React and the router at module scope (imports hoist; position in the
// file buys nothing). That costs this entry nothing today, since both are
// already in it. The reason to import from there is the CONTRACT: pulling
// the tab list in rather than re-typing it here is what keeps the URL's
// idea of a legal tab and the page's idea of a legal tab the same idea.
// The shipped LL97 route spells its list twice, and a tab legal in one
// half and illegal in the other renders a blank surface off a URL the
// router happily kept.
const PortalBuildingDetail = lazy(
  () => import("@/pages/Portal/BuildingDetail/Page"),
);

// A NAMED component, not the inline `component: () => {…}` arrow the other
// portal routes use. `react-hooks/rules-of-hooks` cannot tell an anonymous
// arrow from a plain function, so `Route.useParams()` / `Route.useSearch()`
// inside one are reported as hooks called outside a component — the shipped
// `buildings_/$buildingId/ll97.tsx` carries exactly that error today. This
// is the fix rather than a suppression: it really is a component, and
// saying so makes the rule correct instead of silenced. It reads `Route`,
// declared just below, at RENDER time — long after this module evaluates.
const BuildingDetailRoute: React.FC = () => {
  const { buildingId } = Route.useParams();
  const { tab } = Route.useSearch();
  // THE HASH IS THE OTHER HALF OF THE TAB CONTRACT, so it is read where the
  // rest of the URL is read and threaded down exactly like `tab`. Three
  // shipped links name an element inside a tab body rather than a tab —
  // `#managed-cases` and `#case-<id>` from the FROZEN TrackerSidebar,
  // `#applicable-codes` from the dashboard blocks — and once a body only
  // renders when its tab is selected, the anchor has to be what selects it.
  // Precedence and the reasoning live in `tabs.ts`.
  //
  // `select` narrows the subscription to the hash, so a search-only or
  // param-only navigation does not re-render this wrapper. `location.hash`
  // has NO leading `#` (router-core stores it bare); `tabForHash` tolerates
  // either.
  const hash = useLocation({ select: (l) => l.hash });
  return (
    <Suspense fallback={null}>
      <PortalBuildingDetail buildingId={buildingId} tab={tab} hash={hash} />
    </Suspense>
  );
};

export const Route = createFileRoute("/portal/buildings/$buildingId")({
  // The URL half of the tab contract. An unrecognised `?tab=` is dropped
  // rather than round-tripped; the DEFAULT ("codes") is applied by the
  // render half, so a bare `/portal/buildings/<id>` keeps the clean URL it
  // has always had and still lands on the applicable-codes board.
  //
  // This returns `{ tab }` and nothing else, but an UNDECLARED param is
  // not stripped from the URL — measured live on TanStack 1.134,
  // `?pane=identity` survives both this validator and every subsequent
  // tab click. What a param loses by not being declared here is the TYPE:
  // `Route.useSearch()` is typed to `BuildingDetailRouteSearch`, so the
  // page cannot read it until it is declared.
  validateSearch: (
    search: Record<string, unknown>,
  ): BuildingDetailRouteSearch => validateBuildingDetailSearch(search),
  component: BuildingDetailRoute,
});
